import Link from "next/link";
import { LuClock, LuEye, LuHeart, LuCalendar } from "react-icons/lu";
import { OfficialBadge, LattuMark } from "@/components/articles/OfficialBadge";

const CATEGORY_DISPLAY: Record<string, string> = {
  START_HERE: "Start Here",
  COUNTRIES: "Countries",
  COSTS_AND_BUDGETING: "Costs & Budgeting",
  SCHOLARSHIPS_AND_FUNDING: "Scholarships & Funding",
  VISA_AND_DOCUMENTS: "Visa & Documents",
  TESTS_AND_PREPARATION: "Tests & Preparation",
  FIND_AND_APPLY: "Find & Apply",
  STUDENT_LIFE_ABROAD: "Student Life Abroad",
  WORK_AND_CAREER: "Work & Career",
  PR_AND_SETTLEMENT: "PR & Settlement",
  STUDENT_STORIES: "Student Stories",
  NEWS_AND_UPDATES: "News & Updates",
};

const CATEGORY_COLOR: Record<string, string> = {
  START_HERE: "bg-indigo-50 text-indigo-700 border-indigo-200 dark:bg-indigo-500/10 dark:text-indigo-300 dark:border-indigo-500/30",
  COUNTRIES: "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:border-emerald-500/30",
  COSTS_AND_BUDGETING: "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-500/10 dark:text-amber-300 dark:border-amber-500/30",
  SCHOLARSHIPS_AND_FUNDING: "bg-teal-50 text-teal-700 border-teal-200 dark:bg-teal-500/10 dark:text-teal-300 dark:border-teal-500/30",
  VISA_AND_DOCUMENTS: "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-500/10 dark:text-blue-300 dark:border-blue-500/30",
  TESTS_AND_PREPARATION: "bg-violet-50 text-violet-700 border-violet-200 dark:bg-violet-500/10 dark:text-violet-300 dark:border-violet-500/30",
  FIND_AND_APPLY: "bg-sky-50 text-sky-700 border-sky-200 dark:bg-sky-500/10 dark:text-sky-300 dark:border-sky-500/30",
  STUDENT_LIFE_ABROAD: "bg-pink-50 text-pink-700 border-pink-200 dark:bg-pink-500/10 dark:text-pink-300 dark:border-pink-500/30",
  WORK_AND_CAREER: "bg-orange-50 text-orange-700 border-orange-200 dark:bg-orange-500/10 dark:text-orange-300 dark:border-orange-500/30",
  PR_AND_SETTLEMENT: "bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-500/10 dark:text-purple-300 dark:border-purple-500/30",
  STUDENT_STORIES: "bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-500/10 dark:text-rose-300 dark:border-rose-500/30",
  NEWS_AND_UPDATES: "bg-gray-100 text-gray-700 border-gray-200 dark:bg-white/10 dark:text-gray-300 dark:border-white/20",
};

// Author pages are addressed by the byline itself — neither BlogAuthor nor
// MentorProfile stores a slug. Must stay identical to authorNameToSlug in the
// backend's article.service.ts, which resolves the slug back to an author.
export function authorNameToSlug(name: string): string {
  return name.trim().toLowerCase().replace(/ +/g, "-");
}

// The byline shown for an article, in priority order: the editorial BlogAuthor
// an admin attached, then the mentor who wrote it, then a free-text name.
// `slug` is null when the name is the generic fallback rather than a real
// person, since there is no author page to link to in that case.
export function articleByline(article: {
  blogAuthor?: { name: string } | null;
  mentorProfile?: { displayName: string | null } | null;
  authorName?: string | null;
  addedByAdmin?: unknown;
}): { name: string; slug: string | null; isEditorialFallback: boolean } {
  const named =
    article.blogAuthor?.name ||
    article.mentorProfile?.displayName ||
    article.authorName ||
    null;
  if (named) return { name: named, slug: authorNameToSlug(named), isEditorialFallback: false };
  return article.addedByAdmin
    ? { name: "LattuAI Editorial", slug: null, isEditorialFallback: true }
    : { name: "Uraan Community", slug: null, isEditorialFallback: true };
}

export interface ArticleCardData {
  id: string;
  title: string;
  slug: string;
  excerpt?: string | null;
  coverImageUrl?: string | null;
  coverImageAlt?: string | null;
  categories: string[];
  articleType?: string | null;
  readingTime?: number;
  viewCount?: number;
  likeCount?: number;
  publishedAt?: string | null;
  addedByAdmin?: { id: string; email: string } | null;
  mentorProfile?: { id: string; displayName: string | null } | null;
  blogAuthor?: { id: string; name: string } | null;
  authorName?: string | null;
}

export function ArticleCard({ article, featured = false }: { article: ArticleCardData; featured?: boolean }) {
  const isOfficial = !!article.addedByAdmin;
  const categories = article.categories ?? [];
  const publishedAt = article.publishedAt
    // timeZone pinned so SSR (UTC) and the reader's browser agree on the day —
    // see ArticleDetailClient's publishedAt.
    ? new Date(article.publishedAt).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric", timeZone: "UTC" })
    : null;
  // Not a link: the whole card is already an anchor to the article, and
  // nesting an <a> inside an <a> produces broken markup. The article page's
  // own byline is where the author name is clickable.
  const byline = articleByline(article);

  return (
    <Link
      href={`/blog/${article.slug}`}
      className={`group flex flex-col rounded-2xl border overflow-hidden transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg ${
        isOfficial
          ? "bg-cyan-50/40 dark:bg-cyan-500/5 border-cyan-200 dark:border-cyan-500/30 hover:border-cyan-400 dark:hover:border-cyan-500/60 hover:shadow-cyan-500/10"
          : "bg-white/80 dark:bg-white/5 border-gray-200 dark:border-white/10 hover:border-indigo-300 dark:hover:border-indigo-500/40 dark:hover:shadow-indigo-500/5"
      } ${featured ? "sm:flex-row" : ""}`}
    >
      {article.coverImageUrl && (
        <div className={`overflow-hidden shrink-0 ${featured ? "sm:w-64 h-44 sm:h-auto" : "h-44"}`}>
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={article.coverImageUrl}
            alt={article.coverImageAlt ?? article.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        </div>
      )}
      <div className="flex flex-col gap-3 p-5 flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-1.5 flex-wrap">
            {categories.map((c) => (
              <span key={c} className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${CATEGORY_COLOR[c] ?? CATEGORY_COLOR.NEWS_AND_UPDATES}`}>
                {CATEGORY_DISPLAY[c] ?? c}
              </span>
            ))}
            {article.articleType && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-100 dark:bg-white/10 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-white/15">
                {article.articleType}
              </span>
            )}
            {isOfficial && <OfficialBadge />}
          </div>
          {publishedAt && (
            <span className="flex items-center gap-1 text-xs text-gray-400 dark:text-gray-500">
              <LuCalendar className="w-3 h-3" />
              {publishedAt}
            </span>
          )}
        </div>

        <h3 className={`font-semibold leading-snug transition-colors ${featured ? "text-lg" : "text-base"} ${
          isOfficial
            ? "text-gray-900 dark:text-white group-hover:text-cyan-700 dark:group-hover:text-cyan-300"
            : "text-gray-900 dark:text-white group-hover:text-indigo-600 dark:group-hover:text-indigo-400"
        } line-clamp-2`}>
          {article.title}
        </h3>

        {article.excerpt && (
          <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2 leading-relaxed">
            {article.excerpt}
          </p>
        )}

        <div className="flex items-center justify-between pt-2 border-t border-gray-100 dark:border-white/8 mt-auto">
          <span className="flex items-center gap-1.5 text-xs font-medium truncate min-w-0">
            {isOfficial ? (
              <>
                <LattuMark size={18} />
                <span className="text-cyan-800 dark:text-cyan-200 truncate">{byline.name}</span>
              </>
            ) : (
              <span className="text-gray-500 dark:text-gray-400 truncate">{byline.name}</span>
            )}
          </span>
          <div className="flex items-center gap-3 text-xs text-gray-400 dark:text-gray-500 shrink-0">
            {(article.readingTime ?? 0) > 0 && (
              <span className="flex items-center gap-1">
                <LuClock className="w-3.5 h-3.5" />
                {article.readingTime} min
              </span>
            )}
            <span className="flex items-center gap-1">
              <LuEye className="w-3.5 h-3.5" />
              {article.viewCount ?? 0}
            </span>
            <span className="flex items-center gap-1">
              <LuHeart className="w-3.5 h-3.5" />
              {article.likeCount ?? 0}
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
}

export function ArticleCardSkeleton({ featured = false }: { featured?: boolean }) {
  return (
    <div className={`flex rounded-2xl bg-white/80 dark:bg-white/5 border border-gray-200 dark:border-white/10 overflow-hidden animate-pulse ${featured ? "sm:flex-row" : "flex-col"}`}>
      <div className={`bg-gray-200 dark:bg-gray-700 shrink-0 ${featured ? "sm:w-64 h-44 sm:h-auto" : "h-44 w-full"}`} />
      <div className="flex flex-col gap-3 p-5 flex-1">
        <div className="flex justify-between">
          <div className="h-5 w-24 bg-gray-200 dark:bg-gray-700 rounded-full" />
          <div className="h-4 w-20 bg-gray-200 dark:bg-gray-700 rounded" />
        </div>
        <div className="space-y-2">
          <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-full" />
          <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-3/4" />
        </div>
        <div className="space-y-1.5">
          <div className="h-3.5 bg-gray-200 dark:bg-gray-700 rounded" />
          <div className="h-3.5 bg-gray-200 dark:bg-gray-700 rounded w-5/6" />
        </div>
        <div className="flex justify-between pt-2 border-t border-gray-100 dark:border-white/8">
          <div className="h-3.5 w-24 bg-gray-200 dark:bg-gray-700 rounded" />
          <div className="h-3.5 w-20 bg-gray-200 dark:bg-gray-700 rounded" />
        </div>
      </div>
    </div>
  );
}
